import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { UPDATED_CONTRACT } from '../../../enum/Messages';
import { formatDate, getStatusLabel } from '../../../utils/Functions';
import { Platform } from '../../../model/platform/Platform';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { ContractService, IContractAnnexInfo } from '../ContractService';
import { ErrorToast } from '../../../utils/toasters';
import CardLayout from '../../../shared/components/CardLayout';
import { CancellationModal, ICancellationData } from '../../../shared/components/CancellationModal';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';

export function ContractEdit() {
    const [status, setStatus] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [annexInfo, setAnnexInfo] = useState<IContractAnnexInfo | null>(null);
    const { offerStatus } = useSelector((state: any) => state.enums);
    const {
        state: { offer: offerState, id },
    }: any = useLocation();

    const navigate = useNavigate();

    function editOffer() {
        ContractService.editContracts(id, { ...offerState, status: status })
            .then(() => {
                toast(UPDATED_CONTRACT);
                navigate('/contracts');
            })
            .catch(err => ErrorToast(err));
    }

    function handleChange(evt: any) {
        const value = evt.target.value;
        if (value === 'canceled' && offerState.status !== 'canceled') {
            setShowCancelModal(true);
            return;
        }
        setStatus(value);
    }

    function confirmCancellation(data: ICancellationData) {
        setIsCancelling(true);
        ContractService.cancelContract(id, { note: data.note, file: data.file })
            .then(() => {
                setShowCancelModal(false);
                toast(UPDATED_CONTRACT);
                navigate('/contracts');
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsCancelling(false));
    }

    useEffect(() => {
        setStatus(offerState.status);
    }, [offerState]);

    useEffect(() => {
        ContractService.getContractAnnexInfo(id)
            .then(setAnnexInfo)
            .catch(err => ErrorToast(err));
    }, [id]);

    // An amendment waiting for a signature and the contract it amends are managed through the amendment
    const isPendingAnnex = !!annexInfo?.isAnnex && offerState?.status === 'pre_annex';
    const hasOpenAnnex = !!annexInfo?.openAnnex;
    const isLocked = !!annexInfo?.isAnnex || hasOpenAnnex;
    // Amendment statuses are set by the amendment itself
    const statusOptions = offerStatus.filter(
        (option: string) => !['pre_annex', 'annexed'].includes(option) || option === offerState?.status
    );

    return (
        <CardLayout classBody="fw-bold font-size-h5">
            <CancellationModal
                show={showCancelModal}
                title={`Cancel contract ${offerState?.id ?? ''}`}
                onHide={() => setShowCancelModal(false)}
                onConfirm={confirmCancellation}
                isLoading={isCancelling}
            />
            {annexInfo?.isAnnex && (
                <Alert variant={isPendingAnnex ? 'warning' : 'info'} className="fs-6 fw-normal">
                    This is Amendment {annexInfo.annexNumber} of contract {annexInfo.annexOfId}.{' '}
                    {isPendingAnnex
                        ? 'It is waiting for the influencer to sign it. The brand can edit or delete it.'
                        : `It took effect${
                              annexInfo.annexAcceptedAt ? ` on ${formatDate(annexInfo.annexAcceptedAt)}` : ''
                          } and replaced contract ${annexInfo.annexOfId}.`}{' '}
                    An amendment cannot be changed here.
                </Alert>
            )}
            {annexInfo?.openAnnex && (
                <Alert variant="warning" className="fs-6 fw-normal">
                    Amendment {annexInfo.openAnnex.annexNumber} (contract {annexInfo.openAnnex.id}) of this contract is
                    waiting for a signature. The contract cannot be changed until the amendment is signed or deleted.
                </Alert>
            )}
            {annexInfo?.replacedBy && (
                <Alert variant="info" className="fs-6 fw-normal">
                    This contract was replaced by Amendment {annexInfo.replacedBy.annexNumber} (contract{' '}
                    {annexInfo.replacedBy.id}).
                </Alert>
            )}
            <div className="row">
                <div className="col-6">
                    <p>
                        Contract ID:
                        <br />
                        <span className="text-black-50">{offerState?.id}</span>
                    </p>
                    <p>
                        Date of creation:
                        <br />
                        <span className="text-black-50">{formatDate(offerState?.createdAt)}</span>
                    </p>
                    {offerState?.status === 'canceled' &&
                        (offerState?.cancellationNote || offerState?.cancellationDocumentPath) && (
                            <p>
                                Cancellation:
                                <br />
                                {offerState?.cancellationNote && (
                                    <span className="text-black-50 d-block" style={{ whiteSpace: 'pre-wrap' }}>
                                        {offerState.cancellationNote}
                                    </span>
                                )}
                                {offerState?.cancellationDocumentPath && (
                                    <a href={offerState.cancellationDocumentPath} target="_blank" rel="noreferrer">
                                        {offerState.cancellationDocumentName ?? 'Cancellation PDF'}
                                    </a>
                                )}
                            </p>
                        )}
                    {['accepted', 'completed'].includes(offerState.status) && (
                        <p>
                            Date of acceptance:
                            <br />
                            <span className="text-black-50">{formatDate(offerState?.acceptedDate)}</span>
                        </p>
                    )}
                    <p>
                        Company:
                        <br />
                        <span className="text-black-50">{offerState?.campaign?.company?.displayName}</span>
                    </p>
                    <p>
                        Influencer:
                        <br />
                        <span className="text-black-50">{offerState?.user?.displayName}</span>
                    </p>
                    {offerState?.purchaseOrderInfluencer && (
                        <p>
                            Purchase Order Influencer:
                            <br />
                            <span className="text-black-50">{offerState?.purchaseOrderInfluencer}</span>
                        </p>
                    )}
                    {offerState?.counterContractBy ? (
                        <p>
                            Counter by:
                            <br />
                            <span className="text-black-50">{offerState?.counterContractBy?.displayName}</span>
                        </p>
                    ) : (
                        ''
                    )}
                </div>
                <div className="col-6">
                    <p>
                        Campaign: <br />
                        <span className="text-black-50 word-break">
                            {offerState?.campaign?.id} {offerState?.campaign?.title}
                        </span>
                    </p>
                    <p>
                        Current: <br />
                        {offerState?.isCurrent ? (
                            <span className="word-break text-primary">
                                <FaCheck />
                            </span>
                        ) : (
                            <span className="word-break text-danger">
                                <FaTimes />
                            </span>
                        )}
                    </p>
                    {offerState?.user?.agencyCompany && (
                        <p>
                            Agency:
                            <br />
                            <span className="text-black-50">{offerState?.user?.agencyCompany?.displayName}</span>
                        </p>
                    )}
                    {offerState?.hasInfluencerFee && (
                        <p>
                            Influencer fee percentage:
                            <br />
                            <span className="text-black-50">{offerState?.influencerFeePercentage}%</span>
                        </p>
                    )}
                    <h5 className="font-weight-bold">Platforms</h5>
                    <div className="d-flex align-items-center flex-column flex-md-row">
                        <div className="w-md-75">
                            {offerState?.totalPlatforms?.map((item: Platform) => (
                                <Row key={item.id} className="align-items-center my-2">
                                    <Col md={6} xs={9} className="d-flex align-items-center">
                                        <img src={item.path} width={30} alt={item.name} />
                                        <span className="mb-0 ml-2">{item.name}</span>
                                    </Col>
                                    <Col md={6} xs={2}>
                                        <span className="text-black-50 mb-0 ml-6">x{item.nbPostsTotal}</span>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {offerState?.isCurrent && !isLocked && (
                <>
                    <div>
                        <Form.Label>Status:</Form.Label>
                        <SingleSelectDropdown
                            placeholder="Status"
                            clearable={false}
                            options={statusOptions.map((option: string) => ({
                                value: option,
                                label: getStatusLabel(option),
                            }))}
                            selected={status ?? ''}
                            onChange={value => handleChange({ target: { name: 'status', value } })}
                        />
                    </div>
                    <Button className="col-md-1 col-sm-12 mt-4" onClick={() => editOffer()} size="lg" type="submit">
                        Save
                    </Button>
                </>
            )}
        </CardLayout>
    );
}
